import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { useTranslation } from 'react-i18next'
import { getValueFromResource } from '../../../common/timeLiveFunction'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { MapContainer, TileLayer, Marker, Circle, Polygon, Popup, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import Select from 'react-select'
import 'leaflet/dist/leaflet.css'
import './geofencing.css'
import { GetAccountGeoFenceZoneById, getAccountGeoFenceZoneType, SystemRadiusUnitController } from './GeofencingSlice'
import fetchTimeliveAPI from '../../../common/fetchTimeliveAPI'
import { ShowToast } from '../../../common/Toastr'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Loader from '../../../common/Loader'
import { makeMenuListWithAdd } from './makeMenuListWithAdd'
import { CustomNoOptionsMessage } from './CustomNoOptionsMessage'

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
})

interface PolygonPoint {
    lat: number
    lng: number
}

const WORLD_CENTER_LAT = 20
const WORLD_CENTER_LNG = 0
const WORLD_ZOOM = 2
const USER_ZONE_ZOOM = 14

const getPointLabel = (index: number): string => {
    let label = ''
    let num = index + 1
    while (num > 0) {
        num--
        label = String.fromCharCode(65 + (num % 26)) + label
        num = Math.floor(num / 26)
    }
    return label
}

const MapFit: React.FC<{ lat: number, lng: number, rad: number, hasUserCoords: boolean, zoom: number, polygonPoints?: PolygonPoint[] }> = ({ lat, lng, rad, hasUserCoords, zoom, polygonPoints }) => {
    const map = useMap()
    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                const container = map.getContainer()
                if (!container || !container.offsetParent) return
                map.invalidateSize()
                if (polygonPoints && polygonPoints.length >= 2) {
                    const bounds = L.latLngBounds(polygonPoints.map(p => [p.lat, p.lng] as [number, number]))
                    map.fitBounds(bounds, { padding: [40, 40] })
                } else if (hasUserCoords && rad > 0) {
                    const deg = rad / 111320
                    map.fitBounds(
                        [[lat - deg, lng - deg], [lat + deg, lng + deg]],
                        { padding: [40, 40] }
                    )
                } else {
                    map.setView([lat, lng], zoom)
                }
            } catch (e) { }
        }, 200)
        return () => {
            clearTimeout(timer)
            try { map.stop() } catch (e) { }
        }
    }, [map])
    return null
}

interface ContextMenuState {
    visible: boolean
    x: number
    y: number
    lat: number
    lng: number
}

const MapRightClickHandler: React.FC<{ onRightClick: (lat: number, lng: number, x: number, y: number) => void }> = ({ onRightClick }) => {
    useMapEvents({
        contextmenu(e) {
            e.originalEvent.preventDefault()
            onRightClick(e.latlng.lat, e.latlng.lng, e.originalEvent.clientX, e.originalEvent.clientY)
        },
    })
    return null
}

const PolygonClickHandler: React.FC<{ enabled: boolean, onPointAdd: (lat: number, lng: number) => void }> = ({ enabled, onPointAdd }) => {
    useMapEvents({
        click(e) {
            if (!enabled) return

            onPointAdd(
                Number(e.latlng.lat.toFixed(6)),
                Number(e.latlng.lng.toFixed(6))
            )
        },
    })

    return null
}

interface ZoneFormValues {
    status: boolean
    ZoneName: string
    ZoneCode: string
    AccountGeoFenceZoneTypeId: number
    Latitude: string
    Longitude: string
    radius: string
    ZoneDescription: string
    StreetAddress: string
    City: string
    State: string
    PostalCode: string
    Country: string
    BoundaryType: 'Radius' | 'Polygon'
}
interface ZoneTypeOption {
    label: string;
    value: number;
}

const NONE_OPTION: ZoneTypeOption = { label: 'None', value: 0 }
const MAP_TILE_MODES = [
    { label: 'Satellite', url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}' },
    { label: 'Street', url: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}' },
    // { label: 'Terrain', url: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}' },
]

const initialValues: ZoneFormValues = {
    status: true,
    ZoneName: '',
    ZoneCode: '',
    AccountGeoFenceZoneTypeId: 0,
    Latitude: '',
    Longitude: '',
    radius: '500',
    ZoneDescription: '',
    StreetAddress: '',
    City: '',
    State: '',
    PostalCode: '',
    Country: '',
    BoundaryType: 'Radius'
}

const ZoneFormAndMap: React.FC = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { isLoading, radiusUnit: RadiusUnit } = useAppSelector((state) => state.geofencing);
    const { id } = useParams();
    const zoneId = !!id;
    const [isFetchingLocation, setIsFetchingLocation] = useState(false)
    const [mapKey, setMapKey] = useState(0)
    const [mapTileIndex, setMapTileIndex] = useState(1)
    const [radiusUnit, setRadiusUnit] = useState<number | null>(null);
    const [zoneTypeOptions, setZoneTypeOptions] = useState<ZoneTypeOption[]>([NONE_OPTION]);
    const [defaultCenter, setDefaultCenter] = useState<{ lat: number; lng: number; zoom: number } | null>(null)
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({ visible: false, x: 0, y: 0, lat: 0, lng: 0, })
    const contextMenuRef = useRef<HTMLDivElement>(null)
    const [addingZoneType, setAddingZoneType] = useState(false)
    const [selectedZoneType, setSelectedZoneType] = useState<ZoneTypeOption | null>(null)
    const [radiusInputDisplay, setRadiusInputDisplay] = useState('');
    const radiusByUnitRef = useRef<{ [key: number]: number }>({})
    const isUnitSwitchRef = useRef(false);
    const METERS_PER_FOOT = 0.3048;
    const METERS_PER_MILE = 1609.344;
    const feetToMeters = (ft: number) => ft / METERS_PER_FOOT;
    const milesToMeters = (mi: number) => mi / METERS_PER_MILE;
    const feetUnitId = RadiusUnit?.find((u: any) => u.RadiusUnit?.toLowerCase() === 'feet' || u.RadiusUnit?.toLowerCase() === 'ft')?.SystemRadiusUnitId;
    const milesUnitId = RadiusUnit?.find((u: any) => u.RadiusUnit?.toLowerCase() === 'miles')?.SystemRadiusUnitId;
    const unitLabel = RadiusUnit?.find((item) => item?.SystemRadiusUnitId === radiusUnit)?.RadiusAbbreviation;
    const [polygonPoints, setPolygonPoints] = useState<PolygonPoint[]>([]);
    document.title = `Geofence Zone | Livetecs`;

    useEffect(() => {
        if (isUnitSwitchRef.current) {
            isUnitSwitchRef.current = false
            return
        }
        if (values.radius !== '' && values.radius != null) {
            setRadiusInputDisplay((parseFloat(values.radius) || 0).toFixed(decimalPlaces))
        }
    }, [radiusUnit]);

    const handleZoneTypeAdded = async (name: string) => {
        const trimmedName = name.trim();
        const alreadyExists = zoneTypeOptions.some((opt) => opt.label.toLowerCase() === trimmedName.toLowerCase());
        if (alreadyExists) {
            ShowToast('error', 'Zone type with this name already exists', 'error', 'toast-top-right');
            return;
        }
        setAddingZoneType(true)
        try {
            const res = await fetchTimeliveAPI('api/GeoFenceZoneType', 'POST', { ZoneTypeName: name }, true)
            const json = await res.json()
            if (json?.StatusCode === '200' || json?.StatusCode === '201') {
                const newOption = {
                    label: name,
                    value: Number(json?.Data?.AccountGeoFenceZoneTypeId)
                }
                setZoneTypeOptions(prev => [...prev, newOption])
                setFieldValue('AccountGeoFenceZoneTypeId', Number(newOption.value));
                setSelectedZoneType(newOption);
                ShowToast('Success', 'Zone type added', 'success', 'toast-top-right')
            } else {
                ShowToast('error', json?.ResponseMessage, 'error', 'toast-top-right')
            }
        } catch (e) {
            console.error(e)
        } finally {
            setAddingZoneType(false)
        }
    }

    const CustomMenuList = React.useMemo(() => makeMenuListWithAdd(handleZoneTypeAdded, addingZoneType), [addingZoneType])

    useEffect(() => {
        if (zoneId) {
            setDefaultCenter({ lat: WORLD_CENTER_LAT, lng: WORLD_CENTER_LNG, zoom: WORLD_ZOOM })
            return
        }

        fetch('https://ipinfo.io/json').then(async (res) => {
            if (res.status === 200) {
                const data = await res.json();
                const [latitude, longitude] = data.loc.split(',').map(Number);
                setDefaultCenter({ lat: latitude, lng: longitude, zoom: 13 })
                setFieldValue('Latitude', latitude.toFixed(6))
                setFieldValue('Longitude', longitude.toFixed(6))
                setTimeout(() => setMapKey(k => k + 1), 100)
            } else {
                setDefaultCenter({ lat: WORLD_CENTER_LAT, lng: WORLD_CENTER_LNG, zoom: WORLD_ZOOM })
            }
        })
            .catch(() => {
                setDefaultCenter({ lat: WORLD_CENTER_LAT, lng: WORLD_CENTER_LNG, zoom: WORLD_ZOOM })
            })
    }, [zoneId])

    useEffect(() => {
        const handleClick = () => setContextMenu(m => ({ ...m, visible: false }))
        document.addEventListener('click', handleClick)
        return () => document.removeEventListener('click', handleClick)
    }, [])

    const handleMapRightClick = useCallback((lat: number, lng: number, x: number, y: number) => {
        setContextMenu({ visible: true, x, y, lat, lng })
    }, [])

    const handleCopyCoords = () => {
        const coordText = `${contextMenu.lat.toFixed(6)}, ${contextMenu.lng.toFixed(6)}`
        navigator.clipboard.writeText(coordText).catch(() => {
            const el = document.createElement('textarea')
            el.value = coordText
            document.body.appendChild(el)
            el.select()
            document.execCommand('copy')
            document.body.removeChild(el)
        })
        setContextMenu(m => ({ ...m, visible: false }))
        ShowToast('Success', 'Coordinates copied to clipboard', 'success', 'toast-top-right')
    }

    const handleSetCoords = () => {
        setFieldValue('Latitude', contextMenu.lat.toFixed(6))
        setFieldValue('Longitude', contextMenu.lng.toFixed(6))
        setContextMenu(m => ({ ...m, visible: false }))
        setTimeout(() => setMapKey(k => k + 1), 300)
    }

    useEffect(() => {
        dispatch(SystemRadiusUnitController())
    }, [])

    useEffect(() => {
        if (RadiusUnit?.length > 0 && radiusUnit === null) {
            const feetUnit = RadiusUnit.find((u: any) => u?.RadiusUnit?.toLowerCase() === 'feet' || u?.RadiusUnit?.toLowerCase() === 'ft')
            setRadiusUnit(feetUnit?.SystemRadiusUnitId ?? RadiusUnit[1]?.SystemRadiusUnitId)
        }
    }, [RadiusUnit])

    useEffect(() => {
        if (zoneTypeOptions.length > 0 && selectedZoneType === null) {
            const none = zoneTypeOptions.find(opt => opt.value === 0);
            setSelectedZoneType(none || zoneTypeOptions[0]);
            setFieldValue('AccountGeoFenceZoneTypeId', none?.value ?? zoneTypeOptions[0].value);
        }
    }, [zoneTypeOptions]);

    useEffect(() => {
        dispatch(getAccountGeoFenceZoneType()).unwrap().then((result) => {
            if (result?.StatusCode === '200') {
                const options = result?.Data?.map((item: any) => ({
                    label: item?.ZoneTypeName,
                    value: item?.AccountGeoFenceZoneTypeId
                }))
                const allOptions = [{ label: 'None', value: 0 }, ...(options || [])];
                setZoneTypeOptions(allOptions);
                const noneOption = allOptions.find(o => o.value === 0);
                setSelectedZoneType(noneOption || allOptions[0]);
                setFieldValue('AccountGeoFenceZoneTypeId', noneOption?.value ?? allOptions[0].value)
            }
        })
            .catch((error) => {
                console.log(error);
            })
    }, [])

    useEffect(() => {
        if (values.radius !== '' && values.radius != null) {
            setRadiusInputDisplay((parseFloat(values.radius) || 0).toFixed(decimalPlaces))
        }
    }, [radiusUnit]);

    const getAccountGeoFenceZoneById = async () => {
        try {
            const response = await dispatch(GetAccountGeoFenceZoneById(id));
            if (response?.meta?.requestStatus === 'fulfilled') {
                const data = response?.payload?.Data[0];
                if (data) {
                    setFieldValue('ZoneName', data.ZoneName || '');
                    setFieldValue('ZoneCode', data.ZoneCode || '');
                    setFieldValue('AccountGeoFenceZoneTypeId', Number(data.AccountGeoFenceZoneTypeId) || 0);
                    setZoneTypeOptions(prev => {
                        const matched = prev.find(opt => Number(opt.value) === Number(data.AccountGeoFenceZoneTypeId))
                        setSelectedZoneType(matched ?? NONE_OPTION)
                        return prev
                    })

                    const isPolygon = Number(data.BoundaryType) === 2
                    setFieldValue('BoundaryType', isPolygon ? 'Polygon' : 'Radius')

                    if (isPolygon && Array.isArray(data.PolygonPoints)) {
                        const sortedPoints: PolygonPoint[] = [...data.PolygonPoints]
                            .sort((a: any, b: any) => (a.PointOrder ?? 0) - (b.PointOrder ?? 0))
                            .map((p: any) => ({
                                lat: Number(p.Latitude),
                                lng: Number(p.Longitude),
                            }))
                        setPolygonPoints(sortedPoints)
                        setFieldValue('Latitude', '')
                        setFieldValue('Longitude', '')
                        setFieldValue('radius', '500')
                        setRadiusInputDisplay('500')
                    } else {
                        setPolygonPoints([])
                        setFieldValue('Latitude', data.Latitude?.toString() || '');
                        setFieldValue('Longitude', data.Longitude?.toString() || '');
                        setFieldValue('radius', data.Radius?.toString() || '500');
                        setRadiusInputDisplay(data.Radius?.toString())
                        if (data?.RadiusUnitId != null) {
                            radiusByUnitRef.current = { [data.RadiusUnitId]: Number(data.Radius) }
                        }
                    }

                    setFieldValue('ZoneDescription', data.ZoneDescription || '');
                    setFieldValue('StreetAddress', data.StreetAddress || '');
                    setFieldValue('City', data.City || '');
                    setFieldValue('State', data.State || '');
                    setFieldValue('PostalCode', data.PostalCode || '');
                    setFieldValue('Country', data.Country || '');
                    setFieldValue('status', data.IsDisabled);
                    setRadiusUnit(data?.RadiusUnitId);
                    setTimeout(() => setMapKey(k => k + 1), 300);
                    setTimeout(() => {
                        formik.validateForm();
                    }, 500);
                }
            }
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (zoneId) {
            getAccountGeoFenceZoneById()
        }
    }, [zoneId])

    const getUnitConfig = (unit: any) => {
        if (unit === feetUnitId) return { min: 1, max: 5000, step: 1, default: 500 };
        if (unit === milesUnitId) return { min: 0.01, max: 100, step: 0.01, default: 1 };
        return { min: 10, max: 1000, step: 10, default: 500 };
    };
    const unitConfig = getUnitConfig(radiusUnit);
    const currentUnitLabel = RadiusUnit?.find((u: any) => u?.SystemRadiusUnitId === radiusUnit)?.RadiusUnit;

    const validationSchema = React.useMemo(() => Yup.object({
        ZoneName: Yup.string().required('Zone Name is required'),
        ZoneCode: Yup.string().required('Zone Code is required'),
        Latitude: Yup.number()
            .typeError('Must be a number')
            .test('required-for-radius', 'Latitude is required', function (value) {
                if (this.parent.BoundaryType !== 'Radius') return true
                return value !== undefined && value !== null && !isNaN(value as number)
            })
            .test('range', 'Latitude must be between -90 and 90', function (value) {
                if (this.parent.BoundaryType !== 'Radius') return true
                return value == null || (value >= -90 && value <= 90)
            })
            .test('not-zero', 'Latitude cannot be 0', function (value) {
                if (this.parent.BoundaryType !== 'Radius') return true
                return value !== 0
            }),
        Longitude: Yup.number()
            .typeError('Must be a number')
            .test('required-for-radius', 'Longitude is required', function (value) {
                if (this.parent.BoundaryType !== 'Radius') return true
                return value !== undefined && value !== null && !isNaN(value as number)
            })
            .test('range', 'Longitude must be between -180 and 180', function (value) {
                if (this.parent.BoundaryType !== 'Radius') return true
                return value == null || (value >= -180 && value <= 180)
            })
            .test('not-zero', 'Longitude cannot be 0', function (value) {
                if (this.parent.BoundaryType !== 'Radius') return true
                return value !== 0
            }),
        radius: Yup.number()
            .typeError('Must be a number')
            .test('required-for-radius', 'Radius is required', function (value) {
                if (this.parent.BoundaryType !== 'Radius') return true
                return !!value && value > 0
            })
            .test('within-unit-max', `Maximum allowed radius is ${unitConfig.max} ${currentUnitLabel || ''}`, function (value) {
                if (this.parent.BoundaryType !== 'Radius') return true
                return value == null || value <= unitConfig.max
            })
            .test('within-unit-min', `Minimum allowed radius is ${unitConfig.min} ${currentUnitLabel || ''}`, function (value) {
                if (this.parent.BoundaryType !== 'Radius') return true
                return value == null || value >= unitConfig.min
            }),
    }), [unitConfig.max, unitConfig.min, currentUnitLabel]);

    const formik = useFormik<ZoneFormValues>({
        initialValues,
        validationSchema,
        validateOnMount: true,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values, { setSubmitting, resetForm }) => {

            if (values.BoundaryType === 'Polygon' && polygonPoints.length < 3) {
                ShowToast('error', 'Polygon must have at least 3 points', 'error', 'toast-top-right');
                setSubmitting(false);
                return;
            }

            const basePayload = {
                ZoneName: values.ZoneName,
                ZoneCode: values.ZoneCode,
                AccountGeoFenceZoneTypeId: values.AccountGeoFenceZoneTypeId,
                ZoneDescription: values.ZoneDescription,
                StreetAddress: values.StreetAddress,
                City: values.City,
                State: values.State,
                PostalCode: values.PostalCode,
                Country: values.Country,
                IsDisabled: values.status,
                BoundaryType: values.BoundaryType.toLowerCase() == 'radius' ? 1 : 2,
            };

            let payload;

            if (values.BoundaryType === 'Radius') {

                payload = {
                    ...basePayload,
                    Latitude: values.Latitude?.trim(),
                    Longitude: values.Longitude?.trim(),
                    Radius: Number(values.radius),
                    RadiusUnitId: radiusUnit ?? 0,
                    PolygonPoints: []
                };

            } else {

                payload = {
                    ...basePayload,
                    Latitude: 0,
                    Longitude: 0,
                    Radius: 0,
                    RadiusUnitId: radiusUnit ?? 1,
                    PolygonPoints: polygonPoints.map((point, index) => ({
                        PointOrder: index + 1,
                        PointLabel: getPointLabel(index),
                        Latitude: point.lat,
                        Longitude: point.lng
                    }))
                };
            }

            let response: any;
            try {
                if (zoneId) {
                    response = await fetchTimeliveAPI(`api/GeoFenceZone/UpdateAccountGeoFenceZone/${id}`, 'PUT', payload, true);
                } else {
                    response = await fetchTimeliveAPI('api/GeoFenceZone', 'POST', payload, true);
                }
                const json = await response.json();
                if (json?.StatusCode === '201' || json?.StatusCode === '200') {
                    ShowToast('Success', json.ResponseMessage, "success", "toast-top-right");
                    resetForm();
                    setSelectedZoneType(NONE_OPTION);
                    navigate('/geofenceZone')
                    if (zoneTypeOptions?.length > 0) {
                        setFieldValue('AccountGeoFenceZoneTypeId', zoneTypeOptions[0].value);
                    }
                    setMapKey((k) => k + 1);
                } else {
                    ShowToast('error', json.ResponseMessage, "error", "toast-top-right");
                }
            } catch (error) {
                console.log(error);
            }
        }
    })

    const { values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, isValid, dirty } = formik

    const lat = parseFloat(values.Latitude)
    const lng = parseFloat(values.Longitude)
    const rad = parseFloat(values.radius)

    const isPolygonMode = values.BoundaryType === 'Polygon'

    const getPolygonCentroid = (points: PolygonPoint[]) => {
        if (!points.length) return null
        const sum = points.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 })
        return { lat: sum.lat / points.length, lng: sum.lng / points.length }
    }
    const polygonCentroid = isPolygonMode ? getPolygonCentroid(polygonPoints) : null
    const hasUserCoords = isPolygonMode ? polygonPoints.length > 0 : (!isNaN(lat) && !isNaN(lng))
    const mapValid = isPolygonMode ? polygonPoints.length >= 3 : (hasUserCoords && !isNaN(rad) && rad > 0)
    const mapCenterLat = isPolygonMode ? (polygonCentroid?.lat ?? defaultCenter?.lat ?? WORLD_CENTER_LAT) : (hasUserCoords ? lat : (defaultCenter?.lat ?? WORLD_CENTER_LAT))
    const mapCenterLng = isPolygonMode ? (polygonCentroid?.lng ?? defaultCenter?.lng ?? WORLD_CENTER_LNG) : (hasUserCoords ? lng : (defaultCenter?.lng ?? WORLD_CENTER_LNG))
    const mapZoom = hasUserCoords ? USER_ZONE_ZOOM : (defaultCenter?.zoom ?? WORLD_ZOOM)

    const handleCoordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(e)
        setTimeout(() => setMapKey(k => k + 1), 400)
    }

    const displayRadius = parseFloat(values.radius) || 0;
    const getDecimalPlaces = () => {
        if (radiusUnit === milesUnitId) return 2;
        return 0;
    };
    const decimalPlaces = getDecimalPlaces();
    const radiusMetersForMap = radiusUnit === feetUnitId ? feetToMeters(rad) : radiusUnit === milesUnitId ? milesToMeters(rad) : rad;
    const getRadiusForMap = (radius: number, unitId: number | null) => {
        if (unitId === feetUnitId) {
            return radius * 0.3048;
        }

        if (unitId === milesUnitId) {
            return radius * 1609.344;
        }

        return radius;
    };
    const radiusMetersForMapCircle = getRadiusForMap(rad, radiusUnit);

    const handleFetchLocation = async () => {
        const fetchLat = isPolygonMode ? polygonPoints[0]?.lat : lat
        const fetchLng = isPolygonMode ? polygonPoints[0]?.lng : lng

        if (fetchLat == null || fetchLng == null || isNaN(fetchLat) || isNaN(fetchLng)) return

        setIsFetchingLocation(true)
        try {
            const res = await fetchTimeliveAPI(`api/GeoFenceZone/GetLocationDetailsByCoordinates/${fetchLat}/${fetchLng}`, 'GET', '', true)
            const data = await res.json()
            if (data?.StatusCode === '200') {
                const addr = data.Data || {}
                const fallbackStreetAddress = addr.StreetAddress ? addr.StreetAddress.split(',').slice(0, 2).join(', ').trim() : ''
                const streetData = [
                    addr.HouseNumber ? `${addr.HouseNumber},` : '',
                    addr.Road,
                    addr.NeighbourHood,
                    addr.District,
                    addr.Town,
                ].filter(Boolean).join(' ').trim() || addr.StreetAddress;
                setFieldValue('StreetAddress', streetData)
                setFieldValue('City', addr.City || addr.Town || addr.village || addr.Country || '')
                setFieldValue('State', addr.State || '')
                setFieldValue('PostalCode', addr.PostalCode || '')
                setFieldValue('Country', addr.Country || '')
            }
        } catch (err) {
            console.error('Reverse geocode failed:', err)
        } finally {
            setIsFetchingLocation(false)
        }
    }

    const mapReady = defaultCenter !== null || hasUserCoords

    const handlePolygonPointAdd = useCallback((lat: number, lng: number) => {
        setPolygonPoints(prev => [
            ...prev,
            { lat, lng },
        ]);
    }, []);

    const handleUndoPolygonPoint = () => {
        setPolygonPoints(prev => prev.slice(0, -1))
    }

    const handleClearPolygon = () => {
        setPolygonPoints([])
    }

    const createPolygonPointIcon = (label: string) => {
    const isMultiChar = label.length > 1
    return L.divIcon({
        className: 'gf-polygon-point-marker',
        html: `
        <div class="gf-polygon-point ${isMultiChar ? 'gf-polygon-point--wide' : ''}">
            <span class="gf-polygon-point-label ${isMultiChar ? 'gf-polygon-point-label--small' : ''}">${label}</span>
        </div>
         `,
        iconSize: isMultiChar ? [22, 16] : [16, 16],
        iconAnchor: isMultiChar ? [11, 8] : [8, 8],
    })
}

    return (
        <>
            {isLoading && <Loader />}
            {contextMenu.visible && (
                <div
                    ref={contextMenuRef}
                    className="gf-context-menu"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="gf-context-coords">
                        {contextMenu.lat.toFixed(6)}, {contextMenu.lng.toFixed(6)}
                    </div>
                    <button className="gf-context-item" onClick={handleSetCoords}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
                        </svg>
                        {getValueFromResource('Set as Zone Location', dispatch, t)}
                    </button>
                    <button className="gf-context-item" onClick={handleCopyCoords}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        {getValueFromResource('Copy Coordinates', dispatch, t)}
                    </button>
                </div>
            )}
            <form onSubmit={formik.handleSubmit} noValidate className="gf-root">
                <div className="gf-left">
                    <div className="gf-left__head">
                        <h2 className="gf-left__title">
                            {getValueFromResource('Configure Zone', dispatch, t)}
                        </h2>
                        <p className="gf-left__sub">
                            {getValueFromResource('Set up your geographical boundary parameters', dispatch, t)}
                        </p>
                    </div>

                    {zoneId && (
                        <>
                            <div className="gf-status-row">
                                <span className=' form-label fs-6 fw-normal'>
                                    {getValueFromResource('Status', dispatch, t)}
                                </span>
                                <div className="gf-status-right">
                                    <div
                                         className={`gf-toggle-pill ${values.status ? 'gf-toggle-pill--off' : 'gf-toggle-pill--on'}`}
                                        onClick={() => setFieldValue('status', !values.status)}
                                    >
                                        <div className="gf-toggle-pill__thumb" />
                                    </div>
                                   <span className={values.status ? 'gf-badge-inactive' : 'gf-badge-active'}>
                                        {values.status ? getValueFromResource('Inactive', dispatch, t) : getValueFromResource('Active', dispatch, t)}
                                    </span>
                                </div>
                            </div>

                            <div className="gf-divider" />
                        </>
                    )}

                    <div className="gf-field">
                        <label className="required form-label fs-7 fw-normal">
                            {getValueFromResource('Zone Name', dispatch, t)}
                        </label>
                        <input
                            className="form-control fs-7 fw-normal"
                            autoComplete="off"
                            name="ZoneName"
                            value={values.ZoneName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        {touched.ZoneName && errors.ZoneName && (
                            <span className="gf-error">{errors.ZoneName}</span>
                        )}
                    </div>

                    <div className="gf-row2">
                        <div className="gf-field">
                            <label className="required form-label fs-7 fw-normal">
                                {getValueFromResource('Zone Code', dispatch, t)}
                            </label>
                            <input
                                className="form-control fs-7 fw-normal"
                                autoComplete="off"
                                name="ZoneCode"
                                value={values.ZoneCode}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {touched.ZoneCode && errors.ZoneCode && (
                                <span className="gf-error">{errors.ZoneCode}</span>
                            )}
                        </div>
                        <div className="gf-field">
                            <label className="form-label fs-7 fw-normal">
                                {getValueFromResource('Zone Type', dispatch, t)}
                            </label>
                            <Select
                                className="basic-single fs-7 fw-normal"
                                classNamePrefix="basic-single"
                                name="zoneType"
                                placeholder="Select..."
                                isSearchable
                                options={zoneTypeOptions}
                                components={{
                                    MenuList: CustomMenuList,
                                    NoOptionsMessage: CustomNoOptionsMessage
                                }}
                                onKeyDown={(e) => {
                                    const inputValue = (e.currentTarget.querySelector('input') as HTMLInputElement)?.value || '';
                                    const hasMatch = zoneTypeOptions.some((opt: any) => opt.label.toLowerCase().includes(inputValue.toLowerCase()));

                                    if (e.ctrlKey && e.key === 'Enter' && inputValue.trim()) {
                                        e.preventDefault();
                                        handleZoneTypeAdded(inputValue.trim());
                                    }
                                }}
                                value={selectedZoneType}
                                onChange={(option: ZoneTypeOption | null) => {
                                    setFieldValue('AccountGeoFenceZoneTypeId', option ? option.value : '');
                                    setSelectedZoneType(option);
                                }}
                            />
                            {touched.AccountGeoFenceZoneTypeId && errors.AccountGeoFenceZoneTypeId && (<span className="gf-error">{errors.AccountGeoFenceZoneTypeId}</span>)}
                        </div>
                    </div>

                    <div className="gf-row2">
                        <div className="gf-field">
                            <label className="required form-label fs-7 fw-normal">
                                {getValueFromResource('Latitude', dispatch, t)}
                            </label>
                            <div className="gf-input-icon-wrap">
                                <input
                                    className="form-control fs-7 fw-normal"
                                    autoComplete="off"
                                    name="Latitude"
                                    value={values.Latitude}
                                    onChange={handleCoordChange}
                                    onBlur={handleBlur}
                                    disabled={values.BoundaryType === 'Polygon'}
                                />
                                <i className="ki-duotone ki-geolocation gf-input-icon">
                                    <span className="path1"></span>
                                    <span className="path2"></span>
                                </i>
                            </div>
                            {touched.Latitude && errors.Latitude && (
                                <span className="gf-error">{errors.Latitude}</span>
                            )}
                        </div>
                        <div className="gf-field">
                            <label className="required form-label fs-7 fw-normal">
                                {getValueFromResource('Longitude', dispatch, t)}
                            </label>
                            <div className="gf-input-icon-wrap">
                                <input
                                    className="form-control fs-7 fw-normal"
                                    autoComplete="off"
                                    name="Longitude"
                                    value={values.Longitude}
                                    onChange={handleCoordChange}
                                    onBlur={handleBlur}
                                    disabled={values.BoundaryType === 'Polygon'}
                                />
                                <i className="ki-duotone ki-geolocation gf-input-icon">
                                    <span className="path1"></span>
                                    <span className="path2"></span>
                                </i>
                            </div>
                            {touched.Longitude && errors.Longitude && (
                                <span className="gf-error">{errors.Longitude}</span>
                            )}
                        </div>
                    </div>
                    <div className="gf-field">
                        <label className="required form-label fs-7 fw-normal">
                            {getValueFromResource('Boundary Type', dispatch, t)}
                        </label>

                        <div style={{ display: 'flex', gap: '24px', alignItems: 'center', marginTop: '8px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', fontSize: '13px' }}>
                                <input
                                    type="radio"
                                    name="BoundaryType"
                                    value="Radius"
                                    checked={values.BoundaryType === 'Radius'}
                                    onChange={() => {
                                        setFieldValue('BoundaryType', 'Radius')
                                        setPolygonPoints([])
                                    }}
                                />
                                <span>
                                    {getValueFromResource('Radius', dispatch, t)}
                                </span>
                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', fontSize: '13px' }}>
                                <input
                                    type="radio"
                                    name="BoundaryType"
                                    value="Polygon"
                                    checked={values.BoundaryType === 'Polygon'}
                                    onChange={() => {
                                        setFieldValue('BoundaryType', 'Polygon')
                                    }}
                                />
                                <span>
                                    {getValueFromResource('Polygon', dispatch, t)}
                                </span>
                            </label>
                        </div>
                    </div>

                    {values.BoundaryType === 'Radius' && (<div className="gf-field">
                        <div className="gf-radius-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <label className="form-label fs-7 fw-normal">
                                {getValueFromResource('Radius', dispatch, t)}
                            </label>

                            <div className="gf-unit-toggler">
                                {RadiusUnit.map(unit => (
                                    <button
                                        key={unit?.SystemRadiusUnitId}
                                        type="button"
                                        className={`gf-unit-btn ${radiusUnit === unit?.SystemRadiusUnitId ? 'active' : ''}`}
                                        onClick={() => {
                                            const newUnit = unit?.SystemRadiusUnitId;
                                            if (newUnit === radiusUnit) return;
                                            const currentVal = parseFloat(values.radius);
                                            const currentValid = radiusUnit != null && !isNaN(currentVal) && currentVal >= unitConfig.min && currentVal <= unitConfig.max;
                                            const currentMax = unitConfig.max;
                                            if (radiusUnit != null) {
                                                if (currentValid) {
                                                    radiusByUnitRef.current[radiusUnit] = currentVal;
                                                }
                                            }

                                            const cfg = getUnitConfig(newUnit);
                                            const saved = radiusByUnitRef.current[newUnit];
                                            const nextVal = (saved != null && !isNaN(saved)) ? saved : cfg.default;
                                            const dp = newUnit === milesUnitId ? 2 : 0;
                                            isUnitSwitchRef.current = true;
                                            setRadiusUnit(newUnit);
                                            const isOverMax = nextVal > cfg.max;
                                            setFieldValue('radius', nextVal, false);
                                            setRadiusInputDisplay(nextVal.toFixed(dp));
                                            formik.setFieldTouched('radius', true, false);

                                            if (isOverMax) {
                                                formik.setFieldError('radius', `Maximum allowed radius is ${cfg.max} ${unit?.RadiusUnit}`);
                                            } else {
                                                formik.setFieldError('radius', undefined);
                                            }
                                        }}
                                    >
                                        {unit?.RadiusUnit}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="gf-slider-row" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <svg className="gf-slider-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: '20px', flexShrink: 0 }}>
                                <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" />
                            </svg>

                            <input
                                type="range"
                                className="gf-slider"
                                min={unitConfig.min}
                                max={unitConfig.max}
                                step={unitConfig.step}
                                value={parseFloat(displayRadius.toFixed(decimalPlaces))}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setRadiusInputDisplay(val.toFixed(decimalPlaces));
                                    setFieldValue('radius', val);
                                }}
                            />

                            <div style={{ width: '100px', flexShrink: 0, position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="number"
                                    name="radius"
                                    className="form-control fs-7 fw-normal"
                                    style={{
                                        paddingRight: '45px',
                                        paddingLeft: '10px',
                                        width: '100%',
                                        borderColor: (touched.radius && errors.radius) ? '#f64e60' : '',
                                        boxShadow: 'none',
                                    }}
                                    value={radiusInputDisplay}
                                    step={radiusUnit === milesUnitId ? '0.01' : '1'}
                                    min={unitConfig.min}
                                    max={unitConfig.max}
                                    onChange={(e) => {
                                        const val = parseFloat(e.target.value);
                                        const raw = e.target.value
                                        setRadiusInputDisplay(raw)
                                        if (isNaN(val)) {
                                            setFieldValue('radius', '')
                                            return
                                        } else {
                                            setFieldValue('radius', val);
                                            formik.setFieldTouched('radius', true, false);
                                        }

                                        if (val > unitConfig.max) {
                                            setFieldValue('radius', val, false)
                                            formik.setFieldError('radius', `Maximum allowed radius is ${unitConfig.max} ${currentUnitLabel}`)
                                            return
                                        }
                                        setFieldValue('radius', val);
                                    }}
                                />
                                <span style={{
                                    position: 'absolute',
                                    right: '8px',
                                    fontSize: '11px',
                                    color: '#a1a5b7',
                                    pointerEvents: 'none'
                                }}>
                                    {RadiusUnit?.find((item: any) => item?.SystemRadiusUnitId === radiusUnit)?.RadiusUnit}
                                </span>
                            </div>
                        </div>

                        {touched.radius && errors.radius && (
                            <div style={{ fontSize: '11px', color: '#f64e60', marginTop: '5px' }}>
                                {errors.radius}
                            </div>
                        )}
                    </div>)}

                    <div className="gf-field">
                        <label className="form-label fs-7 fw-normal">
                            {getValueFromResource('Description', dispatch, t)}
                        </label>
                        <textarea
                            className="form-control fs-7 fw-normal"
                            autoComplete="off"
                            name="ZoneDescription"
                            value={values.ZoneDescription}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </div>

                    <div className="gf-separator">
                        <span>{getValueFromResource('Address Details', dispatch, t)}</span>
                    </div>

                    <button
                        type="button"
                        className="gf-fetch-btn"
                        // onClick={handleFetchLocation}
                        onClick={() => handleFetchLocation()}
                        disabled={isFetchingLocation || !mapValid}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
                        </svg>
                        {isFetchingLocation ? getValueFromResource('Fetching...', dispatch, t) : getValueFromResource('Fetch Location', dispatch, t)}
                    </button>

                    <div className="gf-field">
                        <label className="form-label fs-7 fw-normal">
                            {getValueFromResource('Street Address', dispatch, t)}
                        </label>
                        <input
                            className="form-control fs-7 fw-normal"
                            name="StreetAddress"
                            value={values.StreetAddress}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </div>

                    <div className="gf-row2">
                        <div className="gf-field">
                            <label className="form-label fs-7 fw-normal">
                                {getValueFromResource('City', dispatch, t)}
                            </label>
                            <input
                                className="form-control fs-7 fw-normal"
                                name="City"
                                value={values.City}
                                onChange={handleChange}
                                onBlur={handleBlur} />
                        </div>
                        <div className="gf-field">
                            <label className="form-label fs-7 fw-normal">
                                {getValueFromResource('Zip / Postal Code', dispatch, t)}
                            </label>
                            <input
                                className="form-control fs-7 fw-normal"
                                name="PostalCode"
                                value={values.PostalCode}
                                onChange={handleChange}
                                onBlur={handleBlur} />
                        </div>
                    </div>

                    <div className="gf-row2">
                        <div className="gf-field">
                            <label className="form-label fs-7 fw-normal">
                                {getValueFromResource('State / Province', dispatch, t)}
                            </label>
                            <input
                                className="form-control fs-7 fw-normal"
                                name="State"
                                value={values.State}
                                onChange={handleChange}
                                onBlur={handleBlur} />
                        </div>
                        <div className="gf-field">
                            <label className="form-label fs-7 fw-normal">
                                {getValueFromResource('Country', dispatch, t)}
                            </label>
                            <input
                                className="form-control fs-7 fw-normal"
                                name="Country"
                                value={values.Country}
                                onChange={handleChange}
                                onBlur={handleBlur} />
                        </div>
                    </div>

                    <div className="gf-divider" />

                    <div className="gf-footer">
                        <button
                            type="submit"
                            className="gf-btn-save"
                            disabled={isSubmitting || !isValid || !dirty}
                        >
                            {zoneId ? getValueFromResource('Update', dispatch, t) : getValueFromResource('Save', dispatch, t)}
                        </button>
                    </div>

                </div>

                <div className="gf-right">

                    <div className="gf-preview-header">
                        <h2 className="gf-preview-title">
                            {getValueFromResource('Zone Preview', dispatch, t)}
                        </h2>
                        <div className="gf-map-tabs">
                            {MAP_TILE_MODES.map((mode, idx) => (
                                <button
                                    key={mode.label}
                                    type="button"
                                    className={`gf-map-tab${mapTileIndex === idx ? ' gf-map-tab--active' : ''}`}
                                    onClick={() => { setMapTileIndex(idx); setMapKey(k => k + 1) }}
                                >
                                    {getValueFromResource(mode.label, dispatch, t)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="gf-map-area">
                        {!mapReady ? (
                            <div className="gf-map-placeholder">
                                <p className="gf-placeholder-title">Detecting your location…</p>
                            </div>
                        ) : (
                            <div className="gf-map-wrapper">
                                <MapContainer
                                    key={mapKey}
                                    center={[mapCenterLat, mapCenterLng]}
                                    zoom={mapZoom}
                                    style={{ width: '100%', height: '100%' }}
                                    scrollWheelZoom
                                    zoomControl
                                    attributionControl={false}
                                    zoomAnimation={false}
                                    fadeAnimation={false}
                                >
                                    <TileLayer
                                        url={MAP_TILE_MODES[mapTileIndex].url}
                                        maxZoom={19}
                                    />
                                    <MapFit
                                        lat={mapCenterLat}
                                        lng={mapCenterLng}
                                        rad={mapValid && !isPolygonMode ? radiusMetersForMapCircle : 0}
                                        hasUserCoords={hasUserCoords}
                                        zoom={mapZoom}
                                        polygonPoints={isPolygonMode ? polygonPoints : undefined}
                                    />
                                    <MapRightClickHandler onRightClick={handleMapRightClick} />
                                    <PolygonClickHandler
                                        enabled={values.BoundaryType === 'Polygon'}
                                        onPointAdd={handlePolygonPointAdd}
                                    />
                                    {values.BoundaryType === 'Radius' && mapValid && (
                                        <Circle
                                            center={[lat, lng]}
                                            radius={radiusMetersForMapCircle}
                                            pathOptions={{
                                                color: '#2563eb',
                                                fillColor: '#2563eb',
                                                fillOpacity: 0.1,
                                                weight: 2,
                                                dashArray: '6 4',
                                            }}
                                        />
                                    )}
                                    {values.BoundaryType === 'Polygon' && polygonPoints.length >= 2 && (
                                        <Polygon
                                            positions={polygonPoints.map(point => [
                                                point.lat,
                                                point.lng
                                            ])}
                                            pathOptions={{
                                                color: '#2563eb',
                                                fillColor: '#2563eb',
                                                fillOpacity: 0.15,
                                                weight: 2,
                                            }}
                                        />
                                    )}
                                    {values.BoundaryType === 'Radius' && hasUserCoords && (
                                        <Marker
                                            position={[lat, lng]}
                                            draggable={true}
                                            eventHandlers={{
                                                dragend: (e) => {
                                                    const marker = e.target;
                                                    const position = marker.getLatLng();

                                                    setFieldValue('Latitude', position.lat.toFixed(6));
                                                    setFieldValue('Longitude', position.lng.toFixed(6));

                                                    setTimeout(() => setMapKey(k => k + 1), 100);
                                                },
                                            }}
                                        >
                                            <Popup>
                                                <strong>{values.ZoneName || 'Zone'}</strong><br />
                                                {values.ZoneCode && (
                                                    <>
                                                        <em>{values.ZoneCode}</em><br />
                                                    </>
                                                )}
                                                Lat: {lat.toFixed(4)}, Lng: {lng.toFixed(4)}<br />
                                                Radius: {rad}{unitLabel}
                                            </Popup>
                                        </Marker>
                                    )}
                                    {values.BoundaryType === 'Polygon' &&
                                        polygonPoints.map((point, index) => (
                                            <Marker
                                                key={`${point.lat}-${point.lng}-${index}`}
                                                position={[point.lat, point.lng]}
                                                icon={createPolygonPointIcon(getPointLabel(index))}
                                            >
                                                <Popup>
                                                    <strong>Point {getPointLabel(index)}</strong>
                                                    <br />
                                                    Lat: {point.lat.toFixed(6)}
                                                    <br />
                                                    Lng: {point.lng.toFixed(6)}
                                                </Popup>
                                            </Marker>
                                        ))
                                    }

                                </MapContainer>
                                {values.BoundaryType === 'Polygon' && (
                                    <div className="gf-polygon-controls">
                                        <div className="gf-polygon-count">
                                            <span>{polygonPoints.length}</span> Points
                                        </div>

                                        <button
                                            type="button"
                                            className="gf-polygon-control-btn"
                                            onClick={handleUndoPolygonPoint}
                                            disabled={polygonPoints.length === 0}
                                        >
                                            Undo
                                        </button>

                                        <button
                                            type="button"
                                            className="gf-polygon-control-btn"
                                            onClick={handleClearPolygon}
                                            disabled={polygonPoints.length === 0}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                )}

                                {values.ZoneName && (
                                    <div className="gf-map-name-badge">
                                        <span className="gf-badge-dot" />
                                        {values.ZoneName.length > 28 ? values.ZoneName.substring(0, 26) + '…' : values.ZoneName}
                                    </div>
                                )}

                                <div className="gf-map-coord-badge">
                                    <div className="gf-coord-lbl">Coordinates</div>
                                    {isPolygonMode ? (
                                        polygonPoints.length > 0
                                            ? <div>{polygonPoints.length} points plotted</div>
                                            : <div className="gf-coord-tip">Click on map to add points</div>
                                    ) : (
                                        hasUserCoords ? <div>{lat.toFixed(4)}, {lng.toFixed(4)}</div> : <div className="gf-coord-tip">Right-click to pin location</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </form>
        </>
    )
}

export default ZoneFormAndMap